import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';

import AnalyticsContext from 'contexts/AnalyticsContext';
import PerformanceBoardSlideOutContext from 'components/PerformanceBoards/contexts/PerformanceBoardSlideOutContext';
import Row from 'components/Common/Row';
import Typography from 'kingpin/atoms/Typography';
import Button from 'kingpin/atoms/Button';
import Tooltip from 'components/Tooltip';
import useCreateCommentsSection from 'components/PerformanceBoards/hooks/useCreateCommentsSection';

import PermissionGates from 'components/PermissionGates';
import PERMISSIONS from 'permissionDefinitions';
import ContextMenu, { Option } from '../../../../kingpin/atoms/ContextMenu';
import EntityAppLink from './EntityAppLink';
import ViewDriverAppButton from './ViewDriverAppButton';
import DatasetDefinitionsContext from '../../../../contexts/DatasetDefinitionsContext';
import FeatureGatesContext from '../../../../contexts/FeatureGatesContext';

export const List = styled.div`
  display: flex;
  flex-direction: column;

  button {
    width: 100%;
  }
`;

// Wider than the default option width so longer section names (e.g. "Related
// Metric Documents") fit on a single line.
const WIZARD_OPTION_WIDTH = 260;

const WizardItem = ({
  label,
  isDisabled,
  disabledMessage,
}: {
  label: PerformanceBoardTypes.SlideOut.SectionType;
  isDisabled?: boolean;
  disabledMessage?: string;
}) => {
  const { setNavState } = useContext(PerformanceBoardSlideOutContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const createCommentsSection = useCreateCommentsSection();

  const openWidget = useCallback(
    (type: PerformanceBoardTypes.SlideOut.SectionType) => {
      if (type === 'Comments') {
        createCommentsSection();
        trackEvent('Board - Slide out - Comments Widget Added');
      } else {
        setNavState({
          slideoutMode: 'Creating',
          creatingSectionType: type,
        });
        trackEvent('Board - Slide out - Widget type selected', {
          widgetType: type,
        });
      }
    },
    [createCommentsSection, setNavState, trackEvent],
  );

  return (
    <Option
      label={label}
      onClick={() => openWidget(label)}
      isDisabled={isDisabled}
      disabledMessage={disabledMessage}
      width={WIZARD_OPTION_WIDTH}
    />
  );
};

const WizardsList = () => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { performanceFields, board } = useContext(
    PerformanceBoardSlideOutContext,
  );

  const isCommentsSectionExists = useCallback(() => {
    return board.slideout.some((widget) => widget.sectionType === 'Comments');
  }, [board.slideout]);

  const hasPerformanceFields = performanceFields.length !== 0;
  const hasBonusPrograms = bonusPrograms.length > 0;
  // Only offered where a program's bonus config enables recognition payments.
  const { isAdditionalPaymentsEnabled } = useContext(FeatureGatesContext);
  const hasRecognitionPaymentPrograms =
    isAdditionalPaymentsEnabled &&
    bonusPrograms.some((program) => !!program.additionalPayments);

  const items: {
    label: PerformanceBoardTypes.SlideOut.SectionType;
    show: boolean;
    isDisabled?: boolean;
    disabledMessage?: string;
  }[] = [
    {
      label: 'Performance Graph',
      show: true,
      isDisabled: !hasPerformanceFields,
      disabledMessage: !hasPerformanceFields
        ? 'Your Dataset has no performance fields'
        : undefined,
    },
    { label: 'Target Progress', show: hasPerformanceFields },
    { label: 'Related Documents', show: true },
    { label: 'Metric Widget', show: true },
    { label: 'Simple Grid', show: true },
    { label: 'Properties', show: true },
    { label: 'Related Metric Documents', show: hasBonusPrograms },
    { label: 'Bonus Eligibility', show: hasBonusPrograms },
    { label: 'Bonus Total Reward', show: hasBonusPrograms },
    { label: 'Bonus Reward', show: hasBonusPrograms },
    { label: 'Additional Payments', show: hasRecognitionPaymentPrograms },
    {
      label: 'Comments',
      show: true,
      isDisabled: isCommentsSectionExists(),
      disabledMessage: 'Your Slideout already has Comments Widget',
    },
  ];

  const sortedItems = items
    .filter((item) => item.show)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <>
      {sortedItems.map((item) => (
        <WizardItem
          key={item.label}
          label={item.label}
          isDisabled={item.isDisabled}
          disabledMessage={item.disabledMessage}
        />
      ))}
    </>
  );
};

const SlideOutHomeHeader = ({
  title,
  close,
}: {
  title: string;
  close: () => void;
}) => {
  const { trackEvent } = useContext(AnalyticsContext);

  const onAddWidgetClicked = useCallback(() => {
    trackEvent('Board - Slide out - Add Widget Clicked');
  }, [trackEvent]);

  return (
    <Row centerAlign spaceBetween style={{ height: '100%' }}>
      <Row centerAlign style={{ height: '100%' }}>
        <Typography.Header type="H5">{title}</Typography.Header>
      </Row>
      <Row centerAlign style={{ gap: 8 }}>
        <PermissionGates.Has
          requiredPermission={PERMISSIONS.PERFORMANCE_BOARD.MODIFY_WIDGETS}
        >
          <ContextMenu
            buttonType="Ghost"
            icon="add"
            tooltip="Add Widget"
            onOpen={onAddWidgetClicked}
            testId={'add-slideout-widget'}
          >
            <WizardsList />
          </ContextMenu>
        </PermissionGates.Has>
        <EntityAppLink />
        <ViewDriverAppButton />
        <Tooltip content="Close">
          <Button onClick={close} icon="cross" type="Secondary" size="Small" />
        </Tooltip>
      </Row>
    </Row>
  );
};

export default SlideOutHomeHeader;
